import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { format } from 'date-fns';
import { 
  ArrowLeft, 
  Gift, 
  User, 
  BookOpen, 
  Truck, 
  Heart,
  Calendar as CalendarIcon,
  CheckCircle2,
  Upload,
  Package,
  Image as ImageIcon,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ab04b277-779a-46f6-a15c-41b90e7d7254/artifacts/ccdpbqku_TBP-New%20Logo%20Design%20conv%201.png";
const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BOOK_GENRES = [
  "Picture Books",
  "Early Readers",
  "Chapter Books",
  "Fiction",
  "Non-Fiction",
  "Educational",
  "African American Literature",
  "Multicultural/Diverse",
  "STEM/Science",
  "Arts & Crafts",
  "Board Books",
  "Other"
];

export default function DonationForm() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    // Donor Info
    donor_name: '',
    donor_email: '',
    donor_phone: '',
    donor_address: '',
    donor_city: '',
    donor_state: '',
    donor_zip: '',
    organization_name: '',
    
    // Donation Details
    donation_type: '',
    book_quantity: '',
    bookshelf_quantity: '',
    condition: '',
    estimated_value: '',
    description: '',
    age_range: '',
    genres: [],
    
    // Why Donating
    donation_reason: '',
    
    // Tax Receipt
    wants_tax_receipt: true,
    
    // Pickup
    pickup_preferred: true,
    preferred_date: '',
    preferred_time: '',
    pickup_instructions: '',
    
    // Image (Base64)
    image_url: ''
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleGenre = (genre) => {
    setFormData(prev => ({
      ...prev,
      genres: prev.genres.includes(genre)
        ? prev.genres.filter(g => g !== genre)
        : [...prev.genres, genre]
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingImage(true);

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      setImagePreview(base64String);
      updateFormData('image_url', base64String);
      setUploadingImage(false);
    };
    reader.onerror = () => {
      toast.error('Failed to read image file');
      setUploadingImage(false);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImagePreview(null);
    updateFormData('image_url', '');
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.donor_name || !formData.donor_email || !formData.donor_phone) {
          toast.error('Please fill in all required contact fields');
          return false;
        }
        if (!formData.donor_address || !formData.donor_city || !formData.donor_state || !formData.donor_zip) {
          toast.error('Please fill in your complete address');
          return false;
        }
        return true;
      case 2:
        if (!formData.donation_type) {
          toast.error('Please select a donation type');
          return false;
        }
        if (!formData.condition) {
          toast.error('Please select the condition of your donation');
          return false;
        }
        if (formData.donation_type === 'Books' || formData.donation_type === 'Both') {
          if (!formData.book_quantity) {
            toast.error('Please select the quantity of books');
            return false;
          }
        }
        if (formData.donation_type === 'Bookshelves' || formData.donation_type === 'Both') {
          if (!formData.bookshelf_quantity) {
            toast.error('Please enter the number of bookshelves');
            return false;
          }
        }
        return true;
      case 3:
        if (!formData.donation_reason) {
          toast.error('Please tell us why you are donating');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setDatePickerOpen(false);
      setCurrentStep(prev => Math.min(prev + 1, 4));
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    setDatePickerOpen(false);
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API}/donations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          bookshelf_quantity: formData.bookshelf_quantity ? parseInt(formData.bookshelf_quantity) : null,
          estimated_value: formData.estimated_value ? parseFloat(formData.estimated_value) : null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit donation');
      }

      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (error) {
      toast.error(error.message || 'Failed to submit donation');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#f8fafc] to-white">
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-3">
                <img src={LOGO_URL} alt="The Bookshelf Project" className="h-12 w-auto" />
              </Link>
            </div>
          </div>
        </header>

        <div className="max-w-2xl mx-auto px-4 py-16">
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-[#1a3a5c] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Thank You for Your Donation!
              </h1>
              <p className="text-lg text-slate-600 mb-6">
                Your generous donation will help us build in-home libraries for children in need.
              </p>
              <div className="bg-[#5cc8e8]/10 rounded-lg p-6 mb-8">
                <h3 className="font-semibold text-[#1a3a5c] mb-2">What's Next?</h3>
                <ul className="text-left text-slate-600 space-y-2">
                  <li>• You'll receive a confirmation email shortly</li>
                  <li>• Our team will contact you within 2-3 business days</li>
                  {formData.pickup_preferred && <li>• We'll confirm your pickup date and time</li>}
                  {formData.wants_tax_receipt && <li>• Your tax receipt will be sent after we receive your donation</li>}
                </ul>
              </div>
              <Link to="/">
                <Button className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90">
                  Return to Home
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8fafc] to-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="The Bookshelf Project" className="h-12 w-auto" />
            </Link>
            <Link to="/">
              <Button variant="ghost" className="text-slate-600">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 bg-gradient-to-br from-[#1a3a5c] to-[#2a5a8c]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Gift className="w-8 h-8 text-[#5cc8e8]" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Donate Books & Bookshelves
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">
            Your donation helps us build in-home libraries for children and cultivate a love for reading.
          </p>
        </div>
      </section>

      {/* Progress Steps */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            {[
              { num: 1, label: 'Contact Info' },
              { num: 2, label: 'Donation Details' },
              { num: 3, label: 'Your Story' },
              { num: 4, label: 'Pickup/Drop-off' }
            ].map((step, index) => (
              <div key={step.num} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold
                  ${currentStep >= step.num ? 'bg-[#1a3a5c] text-white' : 'bg-slate-200 text-slate-500'}`}>
                  {step.num}
                </div>
                <span className={`ml-2 hidden sm:inline ${currentStep >= step.num ? 'text-[#1a3a5c] font-medium' : 'text-slate-400'}`}>
                  {step.label}
                </span>
                {index < 3 && <div className={`w-12 sm:w-24 h-1 mx-2 ${currentStep > step.num ? 'bg-[#1a3a5c]' : 'bg-slate-200'}`} />}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Card className="border-0 shadow-lg">
          <CardContent className="p-8">
            {/* Step 1: Contact Info */}
            {currentStep === 1 && (
              <div className="space-y-6" data-testid="step-contact">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#5cc8e8]/10 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-[#5cc8e8]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Your Information
                    </h2>
                    <p className="text-slate-500">Tell us about yourself</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Full Name *</Label>
                    <Input
                      value={formData.donor_name}
                      onChange={(e) => updateFormData('donor_name', e.target.value)}
                      placeholder="John Doe"
                      data-testid="donor-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Organization (optional)</Label>
                    <Input
                      value={formData.organization_name}
                      onChange={(e) => updateFormData('organization_name', e.target.value)}
                      placeholder="Company or Organization Name"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.donor_email}
                      onChange={(e) => updateFormData('donor_email', e.target.value)}
                      placeholder="john@example.com"
                      data-testid="donor-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone *</Label>
                    <Input
                      type="tel"
                      value={formData.donor_phone}
                      onChange={(e) => updateFormData('donor_phone', e.target.value)}
                      placeholder="(555) 123-4567"
                      data-testid="donor-phone-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Street Address *</Label>
                  <Input
                    value={formData.donor_address}
                    onChange={(e) => updateFormData('donor_address', e.target.value)}
                    placeholder="123 Main Street"
                    data-testid="donor-address-input"
                  />
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>City *</Label>
                    <Input
                      value={formData.donor_city}
                      onChange={(e) => updateFormData('donor_city', e.target.value)}
                      placeholder="Washington"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State *</Label>
                    <Input
                      value={formData.donor_state}
                      onChange={(e) => updateFormData('donor_state', e.target.value)}
                      placeholder="DC"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>ZIP Code *</Label>
                    <Input
                      value={formData.donor_zip}
                      onChange={(e) => updateFormData('donor_zip', e.target.value)}
                      placeholder="20001"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Donation Details */}
            {currentStep === 2 && (
              <div className="space-y-6" data-testid="step-donation">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#5cc8e8]/10 rounded-full flex items-center justify-center">
                    <Package className="w-6 h-6 text-[#5cc8e8]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Donation Details
                    </h2>
                    <p className="text-slate-500">Tell us about your donation</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <Label>What are you donating? *</Label>
                  <RadioGroup
                    value={formData.donation_type}
                    onValueChange={(value) => updateFormData('donation_type', value)}
                    className="grid grid-cols-3 gap-4"
                  >
                    {['Books', 'Bookshelves', 'Both'].map((type) => (
                      <div key={type}>
                        <RadioGroupItem value={type} id={type} className="peer sr-only" />
                        <Label
                          htmlFor={type}
                          className="flex flex-col items-center justify-center p-4 border-2 rounded-lg cursor-pointer peer-data-[state=checked]:border-[#5cc8e8] peer-data-[state=checked]:bg-[#5cc8e8]/10 hover:bg-slate-50"
                        >
                          {type === 'Books' && <BookOpen className="w-8 h-8 text-[#1a3a5c] mb-2" />}
                          {type === 'Bookshelves' && <Package className="w-8 h-8 text-[#1a3a5c] mb-2" />}
                          {type === 'Both' && <Gift className="w-8 h-8 text-[#1a3a5c] mb-2" />}
                          <span className="font-medium text-[#1a3a5c]">{type}</span>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                {(formData.donation_type === 'Books' || formData.donation_type === 'Both') && (
                  <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                    <h3 className="font-semibold text-[#1a3a5c] flex items-center gap-2">
                      <BookOpen className="w-5 h-5" /> Book Details
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Quantity of Books *</Label>
                        <Select value={formData.book_quantity} onValueChange={(v) => updateFormData('book_quantity', v)}>
                          <SelectTrigger data-testid="book-quantity-select">
                            <SelectValue placeholder="Select quantity" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1-10">1-10 books</SelectItem>
                            <SelectItem value="11-25">11-25 books</SelectItem>
                            <SelectItem value="26-50">26-50 books</SelectItem>
                            <SelectItem value="51-100">51-100 books</SelectItem>
                            <SelectItem value="100+">100+ books</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Target Age Range</Label>
                        <Select value={formData.age_range} onValueChange={(v) => updateFormData('age_range', v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select age range" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0-2">Infants (0-2 years)</SelectItem>
                            <SelectItem value="3-5">Toddlers (3-5 years)</SelectItem>
                            <SelectItem value="6-8">Early Readers (6-8 years)</SelectItem>
                            <SelectItem value="Mixed">Mixed Ages</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Book Genres/Categories</Label>
                      <div className="flex flex-wrap gap-2">
                        {BOOK_GENRES.map((genre) => (
                          <button
                            key={genre}
                            type="button"
                            onClick={() => toggleGenre(genre)}
                            className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                              formData.genres.includes(genre)
                                ? 'bg-[#5cc8e8] text-white'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                          >
                            {genre}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {(formData.donation_type === 'Bookshelves' || formData.donation_type === 'Both') && (
                  <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                    <h3 className="font-semibold text-[#1a3a5c] flex items-center gap-2">
                      <Package className="w-5 h-5" /> Bookshelf Details
                    </h3>
                    <div className="space-y-2">
                      <Label>Number of Bookshelves *</Label>
                      <Input
                        type="number"
                        min="1"
                        value={formData.bookshelf_quantity}
                        onChange={(e) => updateFormData('bookshelf_quantity', e.target.value)}
                        placeholder="Enter quantity"
                        data-testid="bookshelf-quantity-input"
                      />
                    </div>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Condition *</Label>
                    <Select value={formData.condition} onValueChange={(v) => updateFormData('condition', v)}>
                      <SelectTrigger data-testid="condition-select">
                        <SelectValue placeholder="Select condition" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="New">New</SelectItem>
                        <SelectItem value="Gently Used">Gently Used</SelectItem>
                        <SelectItem value="Mixed">Mixed (New & Used)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Estimated Value ($)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.estimated_value}
                      onChange={(e) => updateFormData('estimated_value', e.target.value)}
                      placeholder="Optional"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Description of Items</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => updateFormData('description', e.target.value)}
                    placeholder="Describe the books or bookshelves you're donating (titles, authors, condition details, etc.)"
                    rows={3}
                  />
                </div>

                {/* Photo Upload Section */}
                <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                  <h3 className="font-semibold text-[#1a3a5c] flex items-center gap-2">
                    <ImageIcon className="w-5 h-5" /> Photo of Your Donation (Optional)
                  </h3>
                  <p className="text-sm text-slate-500">
                    Adding a photo helps us prepare for your donation and assess its condition.
                  </p>
                  
                  {imagePreview ? (
                    <div className="relative">
                      <img 
                        src={imagePreview} 
                        alt="Donation preview" 
                        className="w-full max-h-64 object-contain rounded-lg border"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={removeImage}
                        data-testid="remove-image-btn"
                      >
                        <X className="w-4 h-4 mr-1" /> Remove
                      </Button>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-[#5cc8e8] transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="donation-image-upload"
                        data-testid="donation-image-input"
                      />
                      <label 
                        htmlFor="donation-image-upload" 
                        className="cursor-pointer flex flex-col items-center"
                      >
                        {uploadingImage ? (
                          <div className="w-12 h-12 border-4 border-[#5cc8e8] border-t-transparent rounded-full animate-spin mb-4" />
                        ) : (
                          <Upload className="w-12 h-12 text-slate-400 mb-4" />
                        )}
                        <span className="text-sm font-medium text-slate-600">
                          {uploadingImage ? 'Processing...' : 'Click to upload a photo'}
                        </span>
                        <span className="text-xs text-slate-400 mt-1">
                          PNG, JPG, WEBP up to 5MB
                        </span>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Your Story */}
            {currentStep === 3 && (
              <div className="space-y-6" data-testid="step-story">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#5cc8e8]/10 rounded-full flex items-center justify-center">
                    <Heart className="w-6 h-6 text-[#5cc8e8]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Your Story
                    </h2>
                    <p className="text-slate-500">Tell us why you're donating</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Why are you choosing to donate to The Bookshelf Project? *</Label>
                  <Textarea
                    value={formData.donation_reason}
                    onChange={(e) => updateFormData('donation_reason', e.target.value)}
                    placeholder="Share your story or reason for donating..."
                    rows={5}
                    data-testid="donation-reason-textarea"
                  />
                </div>

                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                  <Checkbox
                    id="tax-receipt"
                    checked={formData.wants_tax_receipt}
                    onCheckedChange={(checked) => updateFormData('wants_tax_receipt', checked)}
                  />
                  <div>
                    <Label htmlFor="tax-receipt" className="cursor-pointer font-medium">
                      I would like to receive a tax-deductible receipt
                    </Label>
                    <p className="text-sm text-slate-500 mt-1">
                      The Bookshelf Project is a 501(c)(3) nonprofit. Your donation may be tax-deductible.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Pickup/Drop-off */}
            {currentStep === 4 && (
              <div className="space-y-6" data-testid="step-pickup">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#5cc8e8]/10 rounded-full flex items-center justify-center">
                    <Truck className="w-6 h-6 text-[#5cc8e8]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Pickup or Drop-off
                    </h2>
                    <p className="text-slate-500">How would you like to get your donation to us?</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <Label>Delivery Method</Label>
                  <RadioGroup
                    value={formData.pickup_preferred ? 'pickup' : 'dropoff'}
                    onValueChange={(value) => updateFormData('pickup_preferred', value === 'pickup')}
                    className="grid grid-cols-2 gap-4"
                  >
                    <div>
                      <RadioGroupItem value="pickup" id="pickup" className="peer sr-only" />
                      <Label
                        htmlFor="pickup"
                        className="flex flex-col items-center justify-center p-6 border-2 rounded-lg cursor-pointer peer-data-[state=checked]:border-[#5cc8e8] peer-data-[state=checked]:bg-[#5cc8e8]/10 hover:bg-slate-50"
                      >
                        <Truck className="w-10 h-10 text-[#1a3a5c] mb-2" />
                        <span className="font-medium text-[#1a3a5c]">Schedule Pickup</span>
                        <span className="text-sm text-slate-500 text-center mt-1">We'll come to you</span>
                      </Label>
                    </div>
                    <div>
                      <RadioGroupItem value="dropoff" id="dropoff" className="peer sr-only" />
                      <Label
                        htmlFor="dropoff"
                        className="flex flex-col items-center justify-center p-6 border-2 rounded-lg cursor-pointer peer-data-[state=checked]:border-[#5cc8e8] peer-data-[state=checked]:bg-[#5cc8e8]/10 hover:bg-slate-50"
                      >
                        <Package className="w-10 h-10 text-[#1a3a5c] mb-2" />
                        <span className="font-medium text-[#1a3a5c]">Drop-off</span>
                        <span className="text-sm text-slate-500 text-center mt-1">Bring it to us</span>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {formData.pickup_preferred && (
                  <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                    <h3 className="font-semibold text-[#1a3a5c]">Preferred Pickup Details</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Preferred Date</Label>
                        <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen} modal={true}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={`w-full justify-start text-left font-normal ${!formData.preferred_date ? 'text-muted-foreground' : ''}`}
                              data-testid="pickup-date-btn"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {formData.preferred_date 
                                ? format(new Date(formData.preferred_date), 'MMMM d, yyyy')
                                : 'Select a date'}
                            </Button>
                          </PopoverTrigger>
                          {datePickerOpen && (
                            <PopoverContent className="w-auto p-0" align="start" sideOffset={5} style={{ zIndex: 9999 }}>
                              <Calendar
                                mode="single"
                                selected={formData.preferred_date ? new Date(formData.preferred_date) : undefined}
                                onSelect={(date) => {
                                  updateFormData('preferred_date', date ? date.toISOString() : '');
                                  setDatePickerOpen(false);
                                }}
                                initialFocus
                                disabled={(date) => date < new Date()}
                              />
                            </PopoverContent>
                          )}
                        </Popover>
                      </div>
                      <div className="space-y-2">
                        <Label>Preferred Time</Label>
                        <Select value={formData.preferred_time} onValueChange={(v) => updateFormData('preferred_time', v)}>
                          <SelectTrigger data-testid="pickup-time-select">
                            <SelectValue placeholder="Select time" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Morning (9am-12pm)">Morning (9am-12pm)</SelectItem>
                            <SelectItem value="Afternoon (12pm-4pm)">Afternoon (12pm-4pm)</SelectItem>
                            <SelectItem value="Evening (4pm-7pm)">Evening (4pm-7pm)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Special Instructions</Label>
                      <Textarea
                        value={formData.pickup_instructions}
                        onChange={(e) => updateFormData('pickup_instructions', e.target.value)}
                        placeholder="Gate code, building access, parking instructions, etc."
                        rows={2}
                      />
                    </div>
                  </div>
                )}

                {!formData.pickup_preferred && (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h3 className="font-semibold text-[#1a3a5c] mb-2">Drop-off Information</h3>
                    <p className="text-slate-600">
                      Please contact us at <a href="mailto:info@thebookshelfproject.org" className="text-[#5cc8e8] hover:underline">info@thebookshelfproject.org</a> or 
                      call <strong>(202) 993-4805</strong> to arrange a drop-off time.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              {currentStep > 1 ? (
                <Button variant="outline" onClick={prevStep}>
                  Back
                </Button>
              ) : (
                <div />
              )}
              
              {currentStep < 4 ? (
                <Button onClick={nextStep} className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90">
                  Continue
                </Button>
              ) : (
                <Button 
                  onClick={handleSubmit} 
                  disabled={isSubmitting}
                  className="bg-[#5cc8e8] hover:bg-[#5cc8e8]/90"
                  data-testid="submit-donation-btn"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Donation'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="bg-[#1a3a5c] py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-white/60 text-sm">
            © {new Date().getFullYear()} The Bookshelf Project. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
